/**
 * The app-side Family registry (05 §1.2) — the metadata `GenericList` and
 * `GenericDetail` are driven by.
 *
 * Source: `familyRegistry.json`, a committed snapshot of the authored family
 * documents in `db/mrcs/<system>/families/`, rebuilt by
 * `npm run mrcs:client-registry` and bound to the tree by
 * `scripts/__tests__/mrcs-client-registry.test.js`. It is synchronous and loaded
 * with the bundle, so routes and page chrome render on first paint with no
 * metadata fetch and no loading skeleton.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DERIVED HERE, AND WHY IT HAS TO BE
 *
 * 05 §1.3 sketches a rich registry entry — `list.card` with badge groups, a
 * meta grid and icon slots, `detail.sections` in authored order, `nav.group`,
 * `list.default_limit`, `printings.pin_param`, `ui_engine`. **None of that is in
 * the authored format.** P2 authored what the schema has: attributes with
 * `display_order`, `label`, `datatype`, `show_in_list`, `group`, `render_style`,
 * a register tag and the filter URL contract (db/mrcs/README.md). The format has
 * no `list`, `detail`, or `nav` block at all.
 *
 * So this module derives the display shape from what was authored, and the rules
 * are stated in one place rather than scattered through the components:
 *
 *   • The **name-register attribute is the page title and the card title**, and
 *     never also a row. Every family tags exactly one (`register: "name"`).
 *   • **The list card body is the `show_in_list` attributes**, in
 *     `display_order`, minus the title. `show_in_list` is the authored answer to
 *     "does this belong on a list row" and it already governs the API payload
 *     (04 §4.5), so list card and list payload cannot disagree.
 *   • **Detail sections are the attribute scan**, in `display_order`: a grouped
 *     attribute renders in its group's section, an attribute tagged with the
 *     `description` register or carrying `datatype: richtext` gets its own prose
 *     section, and everything else accumulates into one `rows` section at the
 *     position of its first member. That is 01 §3.5's "a family's detail page is
 *     rendered from one ordered scan of this table", made concrete.
 *   • **`default_limit` is the platform default of 20.** Items' 100 (02b:69) is
 *     not expressible in the authored format; it needs a format field and lands
 *     with the Items conversion.
 *   • **Nav is untouched.** `Layout.js` keeps its hardcoded groups; re-pointing
 *     it at `nav_order` is R1, which is not this slice.
 *
 * Two of those gaps closed at R8a, and both closed in the AUTHORED FORMAT
 * rather than here (v2.3):
 *
 *   • **`show_in_detail`** is the counterpart to `show_in_list`, so an attribute
 *     a family declares for the platform's benefit rather than the reader's —
 *     `canonical_priority`, whose whole purpose is to feed the canonical-printing
 *     election — declines the detail page. It is RENDER-ONLY: the detail API
 *     payload still carries every declared attribute, because letting a
 *     presentation statement amputate the wire would couple the data contract to
 *     a rendering choice. (Allowlist A34, retired.)
 *   • **`render_style: "badge"`** claims the list card's badge slot instead of a
 *     body row, so the shipped Rulebooks abbreviation badge survives the move to
 *     one generic card. (Allowlist A35, narrowed.)
 *
 * What is still not derivable is per-page card LAYOUT — a spell card's four-up
 * meta grid, the component icon row — which needs a layout knob on the group's
 * `render_config` and lands with the Spells conversion. Card membership does not:
 * it is already exactly the group's `show_in_list` members.
 */

import snapshot from './familyRegistry.json';
import { DEFAULT_SYSTEM } from './system';
import { carriesStatBlock, layoutDetailSections, statBlockLayoutOf } from './statBlockLayout';

// ---------------------------------------------------------------------------
// Systems the bundle does not contain (MRCS Phase D)
// ---------------------------------------------------------------------------
//
// The snapshot above is the committed one and stays exactly what it is. A
// user-created system is not in it and never can be — it does not exist when
// the bundle is built — so it arrives at runtime from `GET /api/registry`,
// installed here once by `SystemProvider` before it renders anything.
//
// The merge is a LOOKUP ORDER and not a copy: `systemDoc` prefers the committed
// snapshot, so no runtime payload can shadow a built-in even if one claimed the
// slug. Everything below reads systems through it, which is what keeps the two
// kinds of system one code path rather than two.
//
// The consequence worth stating out loud: for `dnd-3.5` and `pf-1e` nothing
// here changes. No fetch, no promise, no loading state, no re-render — the
// synchronous first paint the parity harness measures is the same function of
// the same JSON it was before.

const runtimeSystems = {};

/** Is this system one the bundle already describes? */
export function isBuiltInSystem(slug) {
  return Boolean(snapshot.systems[slug]);
}

/** Do we know this system's families at all — from either source? */
export function hasSystemRegistry(slug) {
  return isBuiltInSystem(slug) || Boolean(runtimeSystems[slug]);
}

// A homebrew is the second reason a runtime payload exists, and it is the one
// that can concern a BUILT-IN (D5). The committed snapshot describes `dnd-3.5`
// perfectly and describes nobody's homebrew, so when a stack is active the
// runtime document — which the server resolved for that stack, layer fields in
// and hidden base fields out — is the authority for that system and only for as
// long as the stack is. `authoritative` is therefore a property of the INSTALL
// rather than of the system: with no stack nothing calls this for a built-in,
// and the snapshot is read exactly as it always was.
const authoritative = {};

/**
 * Install a runtime registry payload (`GET /api/registry`).
 *
 * The derived-entry cache is dropped because it is keyed by (system, segment)
 * and a segment that resolved to nothing a moment ago may resolve now.
 */
export function installSystemRegistry(slug, payload, { forStack = false } = {}) {
  if (!slug || !payload || !payload.families) return;
  if (isBuiltInSystem(slug) && !forStack) return;
  runtimeSystems[slug] = {
    slug,
    name: payload.name,
    families: payload.families,
    nav: Array.isArray(payload.nav) ? payload.nav : Object.keys(payload.families).sort(),
    // The stat-block layout and combat resources, as the snapshot carries them for a built-in (issue 1591).
    stat_block: payload.stat_block || null,
    combat_resources: (payload.rules && payload.rules.combat && Array.isArray(payload.rules.combat.resources))
      ? payload.rules.combat.resources : [],
  };
  authoritative[slug] = forStack;
  cache.clear();
}

/**
 * The committed document, unless a stack resolved one that supersedes it.
 *
 * The default order is unchanged and deliberately so: no runtime payload can
 * shadow a built-in by claiming its slug. Only a payload installed FOR A STACK
 * wins, which is a thing the browser only ever does about a homebrew its own
 * signed-in owner switched on.
 */
function systemDoc(slug) {
  if (authoritative[slug] && runtimeSystems[slug]) return runtimeSystems[slug];
  return snapshot.systems[slug] || runtimeSystems[slug] || null;
}

/** A system's display name ("D&D 3.5e"), from whichever source describes it. */
export function systemDisplayName(slug) {
  return systemDoc(slug)?.name || slug;
}

/** The platform default page size. Per-Family overrides are not authorable yet. */
export const DEFAULT_LIMIT = 20;

/** The register whose attribute titles the page and the card. */
const NAME_REGISTER = 'name';

/**
 * The register that means "this is the prose a reader came for".
 * `registers.json` states that purpose outright — "the description prose the
 * renderer shows on detail pages and list cards" — so honouring it is reading
 * authored intent, not a special case for a key spelled `description`.
 */
const PROSE_REGISTER = 'description';

/**
 * The render style that claims the list card's BADGE slot rather than a body
 * row (05 §1.3, allowlist A35).
 *
 * It is a SLOT claim, not a section claim: group membership still decides which
 * detail section an attribute belongs to, so `spell.school` is a card badge and
 * a member of the `school_line` composed-line subtitle at the same time, which
 * is what the shipped page renders.
 */
const BADGE_STYLE = 'badge';

/** The authored prose claim (v2.5), beside the two derived proxies above. */
const PROSE_STYLE = 'prose';

// ---------------------------------------------------------------------------
// The filter URL contract (db/mrcs/README.md), derived and never stored
// ---------------------------------------------------------------------------

function urlContract(node) {
  // A `range` filter's BUCKET parameter is the declaring key plus `_filter`,
  // beside the `min_<key>` / `max_<key>` pair its custom span writes. All three
  // are derived rather than authored, because the shipped Items contract is
  // exactly that function of the key — authoring `price_filter` would leave the
  // other two to be derived anyway, from a key the first no longer names.
  const urlKey = node.filter_style === 'range'
    ? `${node.url_key || node.key}_filter`
    : (node.url_key || node.key);
  return {
    urlKey,
    apiKey: node.api_key || urlKey,
    urlAliases: node.url_aliases || [],
    filterPanel: node.filter_panel !== false,
    filterLabel: node.filter_label || node.label || node.key,
  };
}

function normalizeAttribute(node) {
  return {
    key: node.key,
    kind: node.kind,
    datatype: node.datatype || null,
    // A reverse section is a SET by construction — "the feats that require this
    // one" — so it is `many` without anyone authoring it.
    many: node.many === true || node.kind === 'reverse',
    label: node.label,
    abbr: node.abbr || null,
    displayOrder: node.display_order || 0,
    showInList: node.show_in_list === true,
    // v2.3. The default is `true` and is never authored, so the test is for the
    // explicit opt-out rather than for the presence of the key.
    showInDetail: node.show_in_detail !== false,
    sortable: node.sortable === true,
    // The `prose`/`rows` split of 05 §2.1, decided once here so the detail
    // sections and the list card cannot disagree about it. Prose is the text a
    // reader came for, so it renders as a paragraph; everything else renders as
    // a `Label: value` row. Labelling prose is what the shipped pages already
    // avoided, on both the list card and the detail body.
    //
    // v2.5 (R9) adds the AUTHORED claim to the two derived proxies. The
    // proxies — the description register, `datatype: richtext` — were adequate
    // while every prose attribute happened to be one or the other, and Skills
    // is where they fail: nine long plain-text passages, each its own headed
    // card on the shipped page, none of them HTML and only one of them the
    // description.
    isProse: node.kind === 'value'
      && (node.register === PROSE_REGISTER || node.datatype === 'richtext'
        || node.render_style === PROSE_STYLE),
    // Plain-text prose keeps its authored line breaks only where the shipped
    // pages did (05 §2.1's `preserve_whitespace`), and that is exactly the
    // description register: `SkillDetail` wrapped its Description card in
    // `whitespace-pre-wrap` and rendered Action / Try Again / Special / Synergy
    // as flowed paragraphs. Deriving it from the register rather than authoring
    // a per-attribute flag keeps the format from growing a typography knob for
    // a distinction that has one rule.
    preserveWhitespace: node.register === PROSE_REGISTER,
    format: node.format || null,
    // v2.6 (gap G3): the number format and the unit are the attribute's, so
    // the list card and the detail page cannot spell one value two ways.
    unit: node.unit || null,
    // v2.7 (gap G3, the other side of the number): `d8`, `+5`.
    prefix: node.prefix || null,
    // Phase G (G2): the authored DENOMINATION. The loader multiplies by it to
    // normalize `entry_register_value.num_value` to the currency's base unit;
    // the renderer divides by it to get back to the value the author wrote,
    // because display always uses the authored denomination (G3 one layer
    // deeper — a shop row printing `37,080,000` for a ship authored `37.08`
    // with `unit: "MCr"` is the base unit leaking to a reader).
    scale: node.scale || 1,
    groupKey: node.group || null,
    renderStyle: node.render_style || null,
    renderConfig: node.render_config || null,
    isBadge: node.render_style === BADGE_STYLE,
    // v2.4: a badge may ALSO claim the detail page's header cluster. R8a
    // measured that the card slot and the header slot are different sets on the
    // one Family it could grade, so this is authored rather than derived.
    inDetailHeader: (node.render_config || {}).detail_slot === 'header',
    register: node.register || null,
    // A REVERSE section names no target family of its own: it mirrors a
    // reference declared on the other side, so the family it links to is the
    // family that declares that reference (`mirrors: "feat.required_feats"`).
    // Deriving it here is what lets `AttrValue` treat a reverse set exactly
    // like a forward one — the rows are the same shape and the links go to the
    // same place, which is the whole claim `mirrors` makes.
    targetFamily: node.target_family
      || (node.mirrors ? String(node.mirrors).split('.')[0] : null),
    // The reverse attribute's other end, verbatim (`"spell.castable_by"`). The
    // family half is already `targetFamily`; the ATTRIBUTE half is what
    // `bucket-by-link-data` needs, because the filter parameters it links
    // through are the mirrored reference's own URL contract.
    mirrors: node.mirrors || null,
    filterStyle: node.filter_style || null,
    filterValues: node.filter_values || null,
    filterDefault: node.filter_default,
    filterUnfilteredValues: node.filter_unfiltered_values || [],
    filterTerm: node.filter_term,
    linkData: (node.link_data || []).map(normalizeAttribute),
    ...urlContract(node),
  };
}

/**
 * Every filter this Family's list page should render a control for.
 *
 * Collected from three places, because a filter can be declared in three:
 * on an attribute, on a `link_data` field riding on a reference, and on a group
 * whose members it ranges over. `filter_panel: false` is honoured — filterable
 * and *shown in the panel* are different claims.
 */
function derivePanelFilters(doc) {
  const out = [];
  const add = (node, where) => {
    if (!node.filter_style) return;
    const contract = urlContract(node);
    if (!contract.filterPanel) return;
    out.push({ key: node.key, where, style: node.filter_style, ...contract,
      // `label` and `filterLabel` are different words on purpose: the panel
      // heading is the filter's copy ("Spell Levels") while the control's own
      // affordances derive from the attribute ("Level" → the L0..L9 boxes).
      label: node.label || node.key,
      datatype: node.datatype || null,
      // The declaring attribute's own number FORMAT and unit, so a `value-list`
      // control spells an option exactly as the card and the detail spell the
      // value (v2.6, gap G3): a CR option reads `1/2`, never `0.5`.
      format: node.format || null,
      unit: node.unit || null,
      prefix: node.prefix || null,
      // A coded value's display word (`render_config.value_labels`), so an
      // option reads "Mundane" where the card and the detail row do.
      renderConfig: node.render_config || null,
      filterValues: node.filter_values || null,
      filterDefault: node.filter_default,
      unfilteredValues: node.filter_unfiltered_values || [],
      filterUnfilteredValues: node.filter_unfiltered_values || [] });
  };
  for (const group of doc.groups || []) add(group, 'group');
  for (const attr of doc.attributes || []) {
    add(attr, 'attribute');
    for (const child of attr.link_data || []) add(child, `link_data:${attr.key}`);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Detail sections, derived from the ordered attribute scan
// ---------------------------------------------------------------------------

/** A group's authored render style, in the renderer's vocabulary. */
function groupStyle(group) {
  return group.render_style === 'labeled-rows' ? 'rows' : group.render_style;
}

function deriveDetailSections(doc, attributes) {
  const groups = new Map((doc.groups || []).map((g) => [g.key, g]));
  const sections = [];
  let openRows = null;

  const closeRows = () => { openRows = null; };

  for (const attr of attributes) {
    if (attr.register === NAME_REGISTER) continue;             // the page title
    // v2.3, allowlist A34: an attribute the family declares for the PLATFORM
    // rather than the reader opts out here — `canonical_priority` feeds the
    // canonical-printing election and is not a fact about the book. It stays on
    // the wire; only the render drops it, so nothing about the data contract
    // moves when an author changes their mind about a row.
    if (!attr.showInDetail) continue;

    if (attr.groupKey && groups.has(attr.groupKey)) {
      const group = groups.get(attr.groupKey);
      let section = sections.find((s) => s.group === attr.groupKey);
      if (!section) {
        section = {
          kind: 'group', group: attr.groupKey, style: groupStyle(group),
          heading: group.label || null, config: group.render_config || null,
          // The group's authored display FORMULA (05 §1.2, v2.0's `formula`
          // column). Only `stat-grid` reads it, and it is carried on the section
          // rather than fetched from the group at render time so the renderer
          // never reaches back into the raw document.
          formula: group.formula || null,
          members: [],
        };
        sections.push(section);
        closeRows();
      }
      section.members.push(attr);
      continue;
    }

    // `render_style: "badge"` is a SLOT claim, not a section claim (R8a, A35),
    // and WHICH slot it claims on the detail is authored: `detail_slot: header`
    // puts it in the header cluster beside the name, so it must not also appear
    // in the body. Without that, a badge is a card-slot claim only and still
    // renders as an ordinary detail row — which is what production does with a
    // rulebook's abbreviation (authoring call 80), so the two cases are
    // genuinely different and the flag is what tells them apart.
    //
    // A GROUPED badge is untouched either way, which is the rule's other half:
    // `spell.school` is a badge in the header AND a member of the composed
    // school line, and the shipped page renders it in both places — so this
    // check sits after the group branch, not before it.
    //
    // Feats' `categories` is the ungrouped case. It declared `badge` from R9 and
    // derived a `badge`-styled section that `DetailSection` returned null for,
    // so it rendered as nothing and was invisible to every instrument until R12
    // widened the section-style guard to all ten segments. Excluding it only
    // from the reference branch was not enough — it fell through to the scalar
    // rows and rendered `Category: Fighter Bonus Feat, General`, which
    // production does not show either.
    if (attr.renderStyle === 'badge' && attr.inDetailHeader) continue;


    if (attr.isProse) {
      sections.push({ kind: 'prose', style: 'prose', heading: attr.label, attr });
      closeRows();
      continue;
    }

    // A reference carrying its own render style is a SECTION, not a row: the
    // shipped spell detail gives Classes and Domains their own headed block of
    // "target → link data" pairs, which is what `link-list` names (05 §2.2). A
    // reference with NO render style stays an ordinary labelled row, which is
    // what rulebook.edition is and must remain.
    //
    // A REVERSE attribute follows the same rule, and R12 is where it starts to
    // matter: Classes declares two, and `bucket-by-link-data` /
    // `inline-expand` are whole sections. Feats' `required_by` declares no
    // style and stays the labelled row it has been since R9 — the same
    // distinction, read on the other side of the link.
    if ((attr.kind === 'reference' || attr.kind === 'reverse') && attr.renderStyle) {
      sections.push({
        kind: 'reference', style: attr.renderStyle, heading: attr.label, attr,
      });
      closeRows();
      continue;
    }

    if (attr.kind === 'value' && attr.datatype === 'structured') {
      sections.push({ kind: 'table', style: 'table', heading: attr.label, attr });
      closeRows();
      continue;
    }

    if (!openRows) {
      openRows = { kind: 'rows', style: 'rows', heading: null, members: [] };
      sections.push(openRows);
    }
    openRows.members.push(attr);
  }

  return sections;
}

// ---------------------------------------------------------------------------
// The list's sort control, derived
// ---------------------------------------------------------------------------

/**
 * The sort options a list offers (05 §1.3's `list.sort_options`), DERIVED from
 * `sortable` rather than authored.
 *
 * An attribute marked sortable is one the list can be ordered by, which is
 * exactly what a sort option is — so a second authored list would be a spelling
 * of a fact already in the tree, free to drift from it. Every family marks its
 * name attribute sortable, and that is the DEFAULT order rather than an option,
 * so a Family offers a control exactly when it marks a SECOND attribute. Today
 * that is Items and only Items, which is the shipped fact 02b:69 records: it is
 * the one list page with a sort select.
 *
 * The two spellings per attribute (`price_low_high` / `price_high_low`) are the
 * shipped URL contract, derived from the key the same way a range filter's
 * buckets are derived from their boundaries. The copy is the renderer's, as all
 * copy is.
 */
export function deriveSortOptions(attributes, nameAttr) {
  const sortable = attributes.filter((a) => a.sortable && a !== nameAttr);
  if (!sortable.length) return [];
  return [
    { value: '', label: `Sort by ${(nameAttr ? nameAttr.label : 'name').toLowerCase()}` },
    ...sortable.flatMap((attr) => [
      { value: `${attr.key}_low_high`, label: `${attr.label}: Low to High` },
      { value: `${attr.key}_high_low`, label: `${attr.label}: High to Low` },
    ]),
  ];
}

// ---------------------------------------------------------------------------
// The list card's body, in blocks
// ---------------------------------------------------------------------------

/**
 * The card body as an ordered list of BLOCKS (allowlist A35's layout knob).
 *
 * Card MEMBERSHIP was already derivable and is unchanged: it is exactly the
 * `show_in_list` attributes, minus the title and minus the badge slot. What was
 * not expressible is card POSITION — the spell card lays its four `stats`
 * members out as a four-up grid and its six `components` members as a row of
 * on/off icons, and both of those groups render as ordinary sections on the
 * detail page, so `render_style` cannot say it and `display_order` cannot
 * either.
 *
 * `render_config.list_layout` on the GROUP is where it goes, deliberately:
 * 05 §1.3's sketch proposed authoring `list.card.meta_grid` and `icon_groups`
 * as member ARRAYS, which would have been a second source of truth for a fact
 * `show_in_list` already carries — and the two would drift the first time
 * someone added a member.
 *
 * Blocks come out in `display_order`. Where the shipped card disagrees with the
 * detail page about that order — the spell card prints class levels FIRST and
 * the detail page prints them after the components — one `display_order` cannot
 * express both, which is the residual A35 names and still excuses.
 */
function deriveCardBlocks(doc, attributes, nameAttr) {
  const groups = new Map((doc.groups || []).map((g) => [g.key, g]));
  const members = attributes.filter((a) => a.showInList && a !== nameAttr && !a.isBadge);
  const blocks = [];
  let openRows = null;

  for (const attr of members) {
    const group = attr.groupKey ? groups.get(attr.groupKey) : null;
    const layout = group && (group.render_config || {}).list_layout;

    if (layout) {
      let block = blocks.find((b) => b.group === attr.groupKey);
      if (!block) {
        block = {
          kind: layout, group: attr.groupKey, label: group.label || null, members: [],
        };
        blocks.push(block);
        openRows = null;
      }
      block.members.push(attr);
      continue;
    }

    if (attr.kind === 'reference' && attr.renderStyle) {
      blocks.push({ kind: attr.renderStyle, attr, members: [attr] });
      openRows = null;
      continue;
    }

    if (!openRows) {
      openRows = { kind: 'rows', members: [] };
      blocks.push(openRows);
    }
    openRows.members.push(attr);
  }

  return blocks;
}

// ---------------------------------------------------------------------------
// Deriving one registry entry
// ---------------------------------------------------------------------------

/**
 * The PILLAR a modifier-bearing family targets (05 §2.2's `templates-panel`).
 *
 * `MonsterDetail.js:413` gated its Templates panel on `system === 'dnd-3.5'` —
 * the app's only per-system detail conditional, and a hardcoded answer to a
 * data question. What the panel actually needs is: does this system HAVE
 * templates, and is this thing something a template applies to? Both are
 * Pillars, so the gate is a read of the authored tree. pf-1e declares no
 * template family, so today the derived answer is exactly the hardcoded one —
 * and importing pf-1e templates would light the panel up with no code change.
 */
const ENTITY_PILLAR = 'entity';
const TEMPLATE_PILLAR = 'template';

/**
 * @param {object} canonical  a `canonical_families` row (platform identity)
 * @param {object|null} doc   this system's family document, or null when the
 *                            system does not populate the canonical family
 * @param {object|null} fallbackDoc  another system's document, used only for
 *                            page chrome in the empty-family case
 * @param {object} context    facts about the SYSTEM rather than this family —
 *                            whether it declares a Template-Pillar family at
 *                            all, and `statBlock`: its stat-block layout when
 *                            this family's stat block is drawn by it (issue 1591)
 */
export function deriveFamily(canonical, doc, fallbackDoc = null, context = {}) {
  const source = doc || fallbackDoc;
  if (!source) return null;

  const attributes = (source.attributes || []).map(normalizeAttribute)
    .sort((a, b) => (a.displayOrder - b.displayOrder) || a.key.localeCompare(b.key));
  const nameAttr = attributes.find((a) => a.register === NAME_REGISTER) || attributes[0];
  const detailStyle = source.detail_style === 'stat-block' ? 'stat-block' : 'cards';

  return {
    family: source.key,
    segment: canonical.url_segment,
    path: canonical.url_segment,
    labels: { singular: source.name_singular || source.name, plural: source.name },
    navOrder: canonical.nav_order,
    favoritable: canonical.favoritable === true,
    // False in the empty-family case (04 §1.1): the page still renders, with the
    // other system's chrome and an empty list, exactly as it does today.
    populated: Boolean(doc),
    attributes,
    attributesByKey: new Map(attributes.map((a) => [a.key, a])),
    nameAttr,
    filters: derivePanelFilters(source),
    list: {
      // v2.6. 20 is the platform default and is never authored; the shipped
      // Items list opens at 100 (02b:69), which is the one Family that says so.
      defaultLimit: source.default_limit || DEFAULT_LIMIT,
      sortOptions: deriveSortOptions(attributes, nameAttr),
      card: {
        title: nameAttr ? nameAttr.key : 'name',
        // The badge slot and the body are one partition of the show_in_list
        // set, so an attribute cannot render twice on a card and cannot fall
        // out of both. `render_style: badge` is what moves it (A35).
        badges: attributes.filter((a) => a.showInList && a !== nameAttr && a.isBadge),
        bodyRows: attributes.filter((a) => a.showInList && a !== nameAttr && !a.isBadge),
        blocks: deriveCardBlocks(source, attributes, nameAttr),
      },
    },
    detail: {
      // v2.4. `cards` is the derived default; `stat-block` is a different
      // MARKUP (05 §1.3, 02b Part 5 item 2), which is why it is a fact the
      // registry carries rather than a skin the renderer picks.
      style: detailStyle,
      // A family whose entries a token carries draws its stat block under the
      // system's declared headings, exactly as the tabletop does (issue 1591).
      sections: context.statBlock
        ? layoutDetailSections(placeSubtitle(deriveDetailSections(source, attributes), detailStyle), context.statBlock)
        : placeSubtitle(deriveDetailSections(source, attributes), detailStyle),
      // A badge may also claim the header cluster beside the name (v2.4).
      headerBadges: attributes.filter((a) => a.isBadge && a.inDetailHeader),
      // The platform templates panel (05 §2.2). Not a per-Family style: it is a
      // section the platform renders for an Entity-Pillar family in a system
      // that declares Template-Pillar content.
      templatesPanel: (source.pillars || []).includes(ENTITY_PILLAR)
        && context.templatePillarPresent === true,
      // Every SCALAR the detail page shows a reader, in order — the rows plus
      // the header badges, minus the title and the prose. It exists for the
      // Initiative Tracker's quick stat panel (06 §2.4 item 4), which used to
      // dump `Object.entries(stat_block)` for pf-1e and a hardcoded nine-row
      // list for dnd-3.5: two entity-shaped code paths for one question the
      // metadata already answers.
      quickStats: attributes.filter((a) => a.kind === 'value' && !a.isProse
        && a.register !== NAME_REGISTER && (a.showInDetail || a.inDetailHeader)),
    },
  };
}

/**
 * The header SUBTITLE of a stat-block detail, derived rather than authored.
 *
 * A stat-block's banner is a name and one composed line under it — the spell's
 * `Evocation (Fire) [Fire]`, the monster's `Large, Outsider, Always lawful
 * evil`. The rule is positional and needs no new field: in a stat-block detail,
 * a `composed-line` group that sorts BEFORE every other section is the
 * subtitle. Anywhere else, and in a cards detail, a composed line is an
 * ordinary section.
 *
 * Authoring a `placement: "subtitle"` was the alternative and was rejected for
 * the reason R8a rejected authoring a badge's slot: the position is already
 * implied by `display_order`, and a second field saying the same thing is a
 * second thing to keep in sync.
 */
function placeSubtitle(sections, detailStyle) {
  if (detailStyle !== 'stat-block') return sections;
  const [first, ...rest] = sections;
  if (!first || first.kind !== 'group' || first.style !== 'composed-line') return sections;
  return [{ ...first, placement: 'subtitle' }, ...rest];
}

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

const CANONICAL_BY_SEGMENT = new Map(
  snapshot.canonical_families.map((c) => [c.url_segment, c])
);

const cache = new Map();

/**
 * The registry entry for a URL segment under one system.
 *
 * Falls back to another system's document for the page chrome when the active
 * system does not populate the canonical family, so the empty-family case
 * renders its title and its "No <entity> found" card rather than blanking — the
 * parity-preserving branch of 04 §1.1.
 */
export function getFamily(segment, systemSlug = DEFAULT_SYSTEM) {
  const cacheKey = `${systemSlug}::${segment}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const canonical = CANONICAL_BY_SEGMENT.get(segment) || authoredCanonical(segment, systemSlug);
  if (!canonical) return null;

  const key = canonicalToFamilyKey(canonical.key, systemSlug);
  const doc = systemDoc(systemSlug)?.families?.[key] || null;
  let fallback = null;
  if (!doc) {
    for (const slug of Object.keys(snapshot.systems)) {
      const other = snapshot.systems[slug].families[canonicalToFamilyKey(canonical.key, slug)];
      if (other) { fallback = other; break; }
    }
  }

  const sys = systemDoc(systemSlug);
  const entry = deriveFamily(canonical, doc, fallback, {
    templatePillarPresent: Object.values(sys?.families || {})
      .some((f) => (f.pillars || []).includes(TEMPLATE_PILLAR)),
    // The system's stat-block layout (issue 1591), for a family the tabletop
    // draws it for; the empty layout when the system declares none.
    statBlock: doc && sys && carriesStatBlock(doc, (sys.combat_resources || []).map((r) => r && r.stat))
      ? statBlockLayoutOf(sys.stat_block) : null,
  });
  cache.set(cacheKey, entry);
  return entry;
}

/**
 * Which other built-in system has a page at this segment, or null (issue 973).
 *
 * A Family that names its own route (Pathfinder's Glossary, D&D 3.5's
 * Conditions) is unknown to the system that lacks it, so a reader who switches
 * system on that page used to land on "Page Not Found". The recorded answer is
 * the shared families' empty-family rule: the same page, empty, saying where
 * the content is. This names that place. Only a page with a nav position
 * counts, the same test the route applies.
 */
export function familyElsewhere(segment, systemSlug = DEFAULT_SYSTEM) {
  if (getFamily(segment, systemSlug)?.navOrder != null) return null;
  for (const slug of Object.keys(snapshot.systems)) {
    if (slug === systemSlug) continue;
    const family = getFamily(segment, slug);
    if (family && family.navOrder != null) {
      return { system: slug, systemName: snapshot.systems[slug].name || slug, family };
    }
  }
  return null;
}

/**
 * A canonical-family stand-in for a Family that names its OWN route.
 *
 * The server has routed these since v2.10 (`family.url_segment`, consulted only
 * when no canonical family claims the segment); this is the same rule on the
 * client, and it is the whole of how a user-created system gets pages. Since
 * issue #311 the built-ins use it too: the Random Tables family is nobody's
 * canonical slot and authors `url_segment: "rolltables"`, so it resolves here
 * under `dnd-3.5` and `pf-1e` exactly as an authored system's Families do.
 *
 * `nav_order` is its position in the system's own nav list rather than a
 * platform slot, and the platform flags now mirror the server's stand-in
 * (09 Phase F): omnisearch and favourites resolve the system and the stack, so
 * a Family somebody authored is content on them like any other. The
 * cross-system resolver is the exception a LAYER Family keeps — it has no
 * system of its own to name.
 *
 * A Family that named no `url_segment` is matched by its KEY, which is the
 * route the server gives a layer's own Family while its stack is on
 * (`familyRegistry.resolveSegment`). Two spellings of one rule would be one
 * spelling too many: a search result the server routes at a segment the client
 * cannot resolve is a link to a blank page.
 */
function authoredCanonical(segment, systemSlug) {
  const sys = systemDoc(systemSlug);
  if (!sys) return null;
  const docs = Object.values(sys.families || {});
  const doc = docs.find((f) => f.url_segment === segment)
    || docs.find((f) => !f.url_segment && !f.canonical_key && f.key === segment);
  if (!doc) return null;
  const at = (sys.nav || []).indexOf(doc.key);
  return {
    key: doc.key,
    url_segment: segment,
    label: doc.name,
    nav_order: at >= 0 ? at : 999,
    searchable: true,
    favoritable: true,
    // Left false rather than guessed: the authored format carries no marker
    // saying whether a document came from a system or from a layer (it must
    // round-trip byte-identically, and neither has ever needed one), and only a
    // layer Family is unresolvable. Nothing on the client reads this — the
    // cross-system repair asks the server, which can tell — so the conservative
    // value is also the honest one.
    resolvable: false,
  };
}

/**
 * The family KEY a system uses for a canonical slot.
 *
 * They usually match, and one does not: the class slot's canonical key is
 * `class` while both systems author the family as `characterclass` (the v1 table
 * name it converted from). Resolving by `canonical_key` rather than by name is
 * what makes that a non-issue.
 */
function canonicalToFamilyKey(canonicalKey, systemSlug) {
  const families = systemDoc(systemSlug)?.families || {};
  if (families[canonicalKey]) return canonicalKey;
  const match = Object.values(families).find((f) => f.canonical_key === canonicalKey);
  return match ? match.key : canonicalKey;
}

/**
 * The route segment a family key is addressable at, or null when it has none.
 *
 * A lookup family such as `edition` carries `canonical_key: null`, so no
 * `canonical_family` row claims a URL segment for it and there is nowhere to
 * link a reference to it. That is what makes "render this reference as a link or
 * as plain text" a data question rather than a per-family branch.
 *
 * A Family that authors its own `url_segment` (Conditions, Glossary, Random
 * Tables, every Shadowrun family) is addressable there, the route
 * `authoredCanonical` resolves; reading only `canonical_key` left a 3.5
 * abbreviation's Flat-Footed unlinked (issue 1538).
 */
export function segmentForFamilyKey(familyKey, systemSlug = DEFAULT_SYSTEM) {
  const doc = systemDoc(systemSlug)?.families?.[familyKey];
  if (!doc) return null;
  if (doc.url_segment) return doc.url_segment;
  if (!doc.canonical_key) return null;
  const canonical = snapshot.canonical_families.find((c) => c.key === doc.canonical_key);
  return canonical ? canonical.url_segment : null;
}

/**
 * The list route a `badge-links` cluster links THROUGH, and the parameter it
 * carries: `/spells?domain=<slug>` for a deity's domain badges (v2.5, R9).
 *
 * The group authors only `link_filter_key` — the query parameter. The PAGE is
 * derived: it is the Family that declares a filter on that parameter over the
 * same target family, which is precisely the definition of "the list this badge
 * can usefully filter". Authoring the page as a second key would have let the
 * two drift, and this is the one difference the DOM parity diff structurally
 * cannot see — a degraded link and a text node have identical `innerText` — so
 * fewer authored facts that can go stale unobserved is the safer trade.
 *
 * Returns null when nothing declares such a filter, which is the plain-badge
 * degrade 05 §2.2 specifies rather than a broken link.
 */
export function filterRouteFor(targetFamilyKey, urlKey, systemSlug = DEFAULT_SYSTEM) {
  if (!targetFamilyKey || !urlKey) return null;
  const families = systemDoc(systemSlug)?.families || {};
  for (const doc of Object.values(families)) {
    for (const attr of doc.attributes || []) {
      if (attr.kind !== 'reference' || attr.target_family !== targetFamilyKey) continue;
      if (!attr.filter_style) continue;
      if ((attr.url_key || attr.key) !== urlKey) continue;
      const segment = segmentForFamilyKey(doc.key, systemSlug);
      if (segment) return { segment, urlKey };
    }
  }
  return null;
}

/**
 * Where a `bucket-by-link-data` section's links go, DERIVED from `mirrors`
 * (05 §2.2).
 *
 * "Spells by Level" links each bucket to `/spells?class=<this class>&level=<n>`,
 * and every part of that URL is already in the tree on the other side of the
 * mirror: the segment is the mirrored family's, `class` is the mirrored
 * reference's own `url_key` (its alias `class_name` is what the shipped
 * ClassDetail wrote, and R1b recorded the pair), and `level` is the `url_key` of
 * the link-data field the buckets are taken from.
 *
 * Deriving it is the whole reason the section stops being a hardcoded 0–9 grid:
 * the same `link_data` declaration that gives the Spells page its level
 * checkboxes gives this section its buckets, so a bucket that no link carries
 * cannot render (05 §2.2, gap G5).
 *
 * The bucket field is the LOWEST `display_order` link-data field — the same
 * "the first field is the value, the rest are qualifiers" rule `linkDataText`
 * applies — so nothing has to be authored twice.
 */
export function reverseBucketRoute(mirrors, systemSlug = DEFAULT_SYSTEM) {
  if (!mirrors) return null;
  const [familyKey, attrKey] = String(mirrors).split('.');
  const doc = systemDoc(systemSlug)?.families?.[familyKey];
  if (!doc || !attrKey) return null;
  const attr = (doc.attributes || []).find((a) => a.key === attrKey);
  const segment = segmentForFamilyKey(familyKey, systemSlug);
  if (!attr || !segment) return null;
  const bucket = [...(attr.link_data || [])]
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.key.localeCompare(b.key))[0];
  if (!bucket) return null;
  return {
    segment,
    selfKey: attr.url_key || attr.key,
    bucketKey: bucket.url_key || bucket.key,
    bucketAttr: bucket.key,
    bucketLabel: bucket.label || bucket.key,
  };
}

/**
 * The registry entry a family KEY resolves to, for a family the reader never
 * navigates to directly.
 *
 * `inline-expand` needs it: an archetype has no page of its own, and the rows
 * of a class's Archetypes accordion are archetype ENTRIES whose prose the class
 * page renders inline. Which of their attributes is prose is the archetype
 * family's own answer, so it is read from the archetype family rather than
 * re-declared on the class.
 */
/**
 * The canonical (platform) identity a Family key resolves to in a system, or
 * null when the Family is nobody's canonical slot. This is how a feature asks
 * "is this the spell family?" without naming a segment: the key is the
 * author's, the canonical key is the platform's (`canonical_families`).
 */
export function canonicalKeyForFamily(familyKey, systemSlug = DEFAULT_SYSTEM) {
  const doc = systemDoc(systemSlug)?.families?.[familyKey];
  return doc && doc.canonical_key ? doc.canonical_key : null;
}

export function getFamilyByKey(familyKey, systemSlug = DEFAULT_SYSTEM) {
  const doc = systemDoc(systemSlug)?.families?.[familyKey];
  if (!doc) return null;
  return (doc.attributes || []).map(normalizeAttribute)
    .sort((a, b) => (a.displayOrder - b.displayOrder) || a.key.localeCompare(b.key));
}

/**
 * Every Family that has a nav position, in nav order.
 *
 * For a BUILT-IN system: the canonical families with a `nav_order` that this
 * system has a Family for (one whose `canonical_key` fills the slot). Having
 * the Family is the test, not having entries in it: a Family with no rows yet
 * keeps its nav link and serves an empty list (04 §2.1). A slot the system has
 * no Family for at all is a kind the game does not have — a game with no feats
 * or classes — and its link would only ever open an empty page, so it is left
 * out (issue 1545). The two d20 systems fill every slot, so their nav is as
 * before; LayoutNav.test.js pins it.
 *
 * A user-created system has no relationship to the canonical slots at all — its
 * Families name their own routes — so its nav is its OWN Families, in the order
 * the runtime registry gave them. Two rules rather than one, because they are
 * genuinely two questions.
 */
export function allFamilies(systemSlug = DEFAULT_SYSTEM) {
  if (!isBuiltInSystem(systemSlug)) {
    const sys = systemDoc(systemSlug);
    if (!sys) return [];
    return (sys.nav || [])
      .map((key) => {
        const doc = sys.families[key];
        if (!doc) return null;
        const segment = doc.url_segment
          || (doc.canonical_key
            && snapshot.canonical_families.find((c) => c.key === doc.canonical_key)?.url_segment);
        return segment ? getFamily(segment, systemSlug) : null;
      })
      .filter(Boolean);
  }
  const families = Object.values(systemDoc(systemSlug)?.families || {});
  const canonical = snapshot.canonical_families
    .filter((c) => c.nav_order !== null)
    .filter((c) => families.some((f) => f.canonical_key === c.key))
    .sort((a, b) => a.nav_order - b.nav_order)
    .map((c) => getFamily(c.url_segment, systemSlug))
    .filter(Boolean);
  // A built-in Family that is nobody's canonical slot but names its OWN route
  // (v2.10 `url_segment` — first authored for the Random Tables family, issue
  // #311) still deserves a nav link: the server routes it, search serves it,
  // and a page nothing links to is a page that does not exist. They follow the
  // canonical slots, in key order, exactly as a user-created system's own
  // Families follow its nav list.
  const own = families
    .filter((f) => f.url_segment && !f.canonical_key)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((f) => getFamily(f.url_segment, systemSlug))
    .filter(Boolean);
  return [...canonical, ...own];
}

/**
 * The Family whose entries carry a given register, or null.
 *
 * The server generalized the feature endpoints off `monsters` and onto a
 * Pillar+register scan — a CR-bearing entry can live in any Entity-Pillar family
 * (06 §2.3), and `generators.js` tags every row it returns with its own
 * `entity_type` for exactly this reason. The client kept calling
 * `/api/monsters/...` anyway, so it held the server to a contract the server had
 * already stopped requiring, and a system whose creatures are not called
 * "monsters" would have had a working API and a dead UI.
 *
 * Returns the first nav-ordered Family declaring the register, which is
 * deterministic and matches the server's own scan order closely enough for a
 * UI's default. A system declaring none gets null, and the caller degrades —
 * the same graceful-absence rule the register design promises everywhere else.
 */
export function familyWithRegister(register, systemSlug = DEFAULT_SYSTEM) {
  return allFamilies(systemSlug)
    .find((f) => f.attributes.some((a) => a.register === register)) || null;
}

/** Test seam: the derived-entry cache is keyed by (system, segment). */
export function __resetFamilyCache() {
  cache.clear();
}
